import { useEffect, useMemo, useRef } from 'react';

/**
 * MUI Autocomplete can give:
 * - single: T | string | null
 * - multiple: Array<T | string>
 *
 * (string happens with freeSolo; some versions use AutocompleteFreeSoloValueMapping<FreeSolo>,
 * which is effectively string when FreeSolo is true)
 */
type AutocompleteMaybeValue<T> = T | string;
type AutocompleteValue<T> = AutocompleteMaybeValue<T> | AutocompleteMaybeValue<T>[] | null | undefined;

type UseAutocompleteAutoSelectSingleOptionProps<T> = {
    /** Master enable flag (e.g. autoSelectSingle) */
    enabled: boolean;

    /** Whether auto-select is allowed right now (combined guard) */
    condition: boolean;

    /** Options to inspect */
    options: readonly T[];

    /** Current value (supports freeSolo strings) */
    value: AutocompleteValue<T>;

    /** Single vs multiple mode */
    multiple: boolean;

    /** Selection handler */
    onChange: (next: T | T[]) => void;

    /** Unique key extractor */
    getKey: (option: T) => string | number;
};

export function useAutocompleteAutoSelectSingleOption<T>({
    enabled,
    condition,
    options,
    value,
    multiple,
    onChange,
    getKey,
}: UseAutocompleteAutoSelectSingleOptionProps<T>) {
    // Prevent repeated auto-selects for the same only-option across refetches/rerenders
    const lastAutoSelectedKeyRef = useRef<string | number | null>(null);

    const isAlreadySelected = useMemo(() => {
        const keyOf = (o: T) => getKey(o);

        if (multiple) {
            const current = Array.isArray(value) ? value : [];

            // only keep real options (ignore freeSolo strings)
            const currentOptions = current.filter((v): v is T => typeof v !== 'string');

            const keys = new Set(currentOptions.map(keyOf));
            return (only: T) => keys.has(keyOf(only));
        }

        const current = Array.isArray(value) ? null : value;

        // if current is freeSolo string, don't override it
        if (typeof current === 'string') {
            return (_only: T) => false;
        }

        const currentKey = current ? keyOf(current) : null;
        return (only: T) => (currentKey ?? null) === keyOf(only);
    }, [multiple, value, getKey]);

    useEffect(() => {
        if (!enabled || !condition) return;
        if (options.length !== 1) return;

        const only = options[0];
        if (!only) return;

        const onlyKey = getKey(only);

        if (isAlreadySelected(only)) return;
        if (lastAutoSelectedKeyRef.current === onlyKey) return;

        lastAutoSelectedKeyRef.current = onlyKey;
        onChange(multiple ? [only] : only);
    }, [enabled, condition, options, multiple, onChange, isAlreadySelected, getKey]);
}
